import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { parseReportDateString } from 'business/shift-report/report/services/timeOperations';
import { ReportChartMode } from 'business/shift-report/report/types';
import {
  ActivityTreeFullFragment,
  ShiftFullFragment,
  TaskFullFragment,
} from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';

import './index.scss';
import { getChartMetadata } from './report-chart-metadata';
import { createShiftReportCharts, updateShiftReportCharts } from './services';
import { SELECTOR } from './services/metadata/chart';
import { ExternalEventHandlers } from './services/metadata/types';

interface Props {
  shift: ShiftFullFragment;
  date: string;
  activities: ActivityTreeFullFragment[];
  tasks: TaskFullFragment[];
  externalEventHandlers?: ExternalEventHandlers;
  criticalPathValid: boolean;
  mode?: ReportChartMode;
}

function ShiftReportChart({
  shift,
  date,
  activities,
  tasks,
  externalEventHandlers,
  criticalPathValid,
  mode = 'edit',
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [forceUpdate, setForceUpdate] = useState({});
  const { isMobile, isTablet } = useMediaType();

  // Create/Update chart based on tasks
  useLayoutEffect(() => {
    const metadata = getChartMetadata({
      activities,
      tasks,
      mode,
      node: ref.current?.parentElement ?? undefined,
    });
    if (!isMounted) {
      createShiftReportCharts({
        metadata,
        shift,
        tasks,
        externalEventHandlers,
        date: parseReportDateString(date, shift.startTime),
        isTablet: isTablet || isMobile,
        criticalPathValid,
        mode,
      });
      setIsMounted(true);
    } else {
      updateShiftReportCharts({
        metadata,
        shift,
        tasks,
        externalEventHandlers,
        date: parseReportDateString(date, shift.startTime),
        isTablet: isTablet || isMobile,
        criticalPathValid,
        mode,
      });
    }
  }, [
    forceUpdate,
    shift,
    tasks,
    activities,
    isMounted,
    externalEventHandlers,
    date,
    isMobile,
    isTablet,
    criticalPathValid,
    mode,
  ]);

  // Check window resize
  useLayoutEffect(() => {
    function handleResize() {
      setForceUpdate({});
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check orientation change
  useEffect(() => {
    function handleOrientationUpdate() {
      setForceUpdate({});
    }

    const screenOrientation = window.screen.orientation;
    if (!screenOrientation) {
      return () => {};
    }

    screenOrientation.addEventListener('change', handleOrientationUpdate);
    return () => {
      screenOrientation.removeEventListener('change', handleOrientationUpdate);
    };
  }, []);

  return <div ref={ref} id={`${SELECTOR}-${mode}`} />;
}

export default ShiftReportChart;
